import { FitAddon } from '@xterm/addon-fit';
import { Terminal } from '@xterm/xterm';
import { $ } from './dom.ts';
import type { Message } from './evaluator-types.ts';

/*
 * The transport-agnostic core of an interactive terminal pane: one xterm.js
 * terminal with cooked-mode line editing on the main thread (a small readline:
 * Emacs keys + cursor movement + history), plus the run/finish lifecycle.
 *
 * What actually runs the program lives behind the TerminalBackend interface:
 * interactive-python.ts backs it with a Pyodide worker and a SharedArrayBuffer
 * stdin handshake; interactive-java.ts backs it with a WebSocket to the Java
 * runner, which executes the program as a subprocess.
 */

export type DataFile = { name: string; data: ArrayBuffer };

export type { Message };

/*
 * Remove the REPL tab (JShell on Java pages, the Python REPL on Python pages)
 * from the terminal pane, for assignments whose config.json sets
 * `replTab: false`. The layouts always render the tab, so this is the one
 * place it gets dropped; marking the terminal tab/view active covers a page
 * whose markup had made the REPL tab the initial one.
 */
export const removeReplTab = () => {
  const doomed = document.querySelectorAll(
    '.terminal-tabs .tab[data-tab="repl"], .terminal-view[data-view="repl"], .terminal-tabs .restart-repl',
  );
  for (const el of doomed) el.remove();
  document.querySelector('.terminal-tabs .tab[data-tab="terminal"]')?.classList.add('active');
  document.querySelector('.terminal-view[data-view="terminal"]')?.classList.add('active');
};

export interface TerminalBackend {
  // Wire the engine in; called once from the engine's constructor. This is
  // where a backend allocates its resources (worker, buffers) and, if it
  // can't run in this browser, calls engine.disable().
  attach(engine: TerminalEngine): void;

  // Raw passthrough mode, for backends whose program runs on a real PTY and
  // does its own echo and line editing (the jshell tab). When true, the
  // engine's readline is bypassed entirely: every byte the user types goes to
  // sendRaw() verbatim (^C, arrows, tab, all of it) and terminal resizes are
  // reported via onTermResize().
  raw?: boolean;

  sendRaw?(data: string): void;

  onTermResize?(cols: number, rows: number): void;

  // Resolves when the backend can accept a run (e.g. Pyodide has loaded).
  whenReady(): Promise<void>;

  // Begin a run. The engine has already reset the terminal and line state and
  // set state to 'running'; the backend calls engine.finishRun() when done.
  start(code: string, dataFiles: DataFile[]): void;

  // One committed input line (no trailing newline).
  sendLine(line: string): void;

  // Ctrl-D on an empty line.
  sendEOF(): void;

  // Ctrl-C. The engine has already echoed ^C and cleared the input line.
  onCtrlC(): void;

  // The Stop button.
  stop(): void;

  // Tear down the current run unconditionally (used for rerun-while-running).
  hardStop(): void;
}

type RunResolve = () => void;

export class TerminalEngine {
  backend: TerminalBackend;
  message: Message;

  term: Terminal;
  fit: FitAddon;

  state: 'loading' | 'idle' | 'running' = 'loading';
  usable = true;

  // What to print when a run finishes (null for the REPL, which never
  // "finishes" in a way the user needs told about).
  finishMessage: string | null;

  // Line discipline (a small readline: Emacs keys + cursor movement + history).
  lineBuffer = '';
  cursor = 0; // insertion point within lineBuffer
  history: string[] = []; // persists across runs, so a REPL restart keeps history
  histIndex = 0; // browsing position; === history.length means "the live draft"
  draft = ''; // the in-progress line stashed when you start browsing history

  runResolve: RunResolve | null = null;

  // When the user last typed (or pasted) into the terminal, and who is
  // waiting for the next time they do — the session loop that parked an
  // idle REPL (keepSessionAlive in interactive-java.ts) wakes on it.
  lastInputAt = Date.now();
  private inputWaiters = new Set<() => void>();

  constructor(
    mount: string,
    backend: TerminalBackend,
    message: Message,
    finishMessage: string | null,
  ) {
    this.backend = backend;
    this.message = message;
    this.finishMessage = finishMessage;

    this.term = new Terminal({
      convertEol: true, // map the program's '\n' to CRLF so lines don't stairstep
      cursorBlink: true,
      fontSize: 14,
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
      theme: { background: '#1e1e1e', foreground: '#e0e0e0' },
    });
    this.fit = new FitAddon();
    this.term.loadAddon(this.fit);
    const container = $(mount) as HTMLElement;
    this.term.open(container);
    this.refit();
    requestAnimationFrame(() => this.refit());
    // Refit whenever the container's size changes for any reason — collapsing
    // the instructions pane, resizing the window — not just the tab switches
    // that call refit() explicitly. Hidden (0×0) containers are fine: refit()
    // swallows the failed fit, and the tab switch refits on reveal.
    new ResizeObserver(() => this.refit()).observe(container);
    this.term.onData((d) => this.onData(d));
    this.term.onResize(({ cols, rows }) => this.backend.onTermResize?.(cols, rows));

    backend.attach(this);
  }

  // Called by a backend that can't run in this browser (e.g. no
  // SharedArrayBuffer). Prints the reason in red and inerts the terminal.
  disable(...reason: string[]) {
    this.usable = false;
    for (const line of reason) {
      this.term.writeln(`\x1b[31m${line}\x1b[0m`);
    }
  }

  refit() {
    try {
      this.fit.fit();
    } catch {
      /* terminal not visible yet; ignore */
    }
  }

  focus() {
    this.term.focus();
  }

  // Resolve the next time the user types anything, in any state (a parked
  // terminal isn't running). Returns a function that withdraws the wait.
  nextInput(): { promise: Promise<void>; cancel: () => void } {
    let waiter: () => void = () => {};
    const promise = new Promise<void>((resolve) => {
      waiter = resolve;
      this.inputWaiters.add(waiter);
    });
    return { promise, cancel: () => this.inputWaiters.delete(waiter) };
  }

  ////////////////////////////////////////////////////////////////////////////
  // Input: a small readline on the main thread. Emacs-style movement/editing
  // keys (Ctrl-A/E/B/F, Ctrl-K/U/W, Alt-B/F) plus arrow keys, and command
  // history on Up/Down (and Ctrl-P/N). All cursor moves are RELATIVE to where the
  // input started (right after the program's prompt), so we never need to know
  // the prompt text and never disturb it. Assumes the line doesn't wrap — fine
  // for a REPL; very long lines are the known limitation.

  onData(data: string) {
    this.lastInputAt = Date.now();
    if (this.inputWaiters.size > 0) {
      const waiters = [...this.inputWaiters];
      this.inputWaiters.clear();
      for (const w of waiters) w();
    }
    if (this.state !== 'running') return;
    if (this.backend.raw) {
      // PTY passthrough: the program echoes and line-edits; we just forward.
      this.backend.sendRaw?.(data);
      return;
    }
    let i = 0;
    while (i < data.length) {
      const code = data.charCodeAt(i);
      if (code === 0x1b) {
        i += this.handleEscape(data, i);
        continue;
      }
      i += 1;
      switch (code) {
        case 0x0d:
        case 0x0a:
          this.commitLine();
          break;
        case 0x7f:
        case 0x08:
          this.backspace();
          break;
        case 0x03:
          this.onCtrlC();
          break;
        case 0x04:
          if (this.lineBuffer.length === 0) this.backend.sendEOF();
          else this.deleteChar(); // Ctrl-D: delete under cursor, EOF on empty line
          break;
        case 0x01:
          this.moveTo(0);
          break; // Ctrl-A
        case 0x05:
          this.moveTo(this.lineBuffer.length);
          break; // Ctrl-E
        case 0x02:
          this.moveTo(this.cursor - 1);
          break; // Ctrl-B
        case 0x06:
          this.moveTo(this.cursor + 1);
          break; // Ctrl-F
        case 0x10:
          this.historyPrev();
          break; // Ctrl-P
        case 0x0e:
          this.historyNext();
          break; // Ctrl-N
        case 0x0b:
          this.setLine(this.lineBuffer.slice(0, this.cursor), this.cursor);
          break; // Ctrl-K
        case 0x15:
          this.setLine(this.lineBuffer.slice(this.cursor), 0);
          break; // Ctrl-U
        case 0x17:
          this.killWordBack();
          break; // Ctrl-W
        default:
          if (code >= 0x20) this.insert(data[i - 1]);
      }
    }
  }

  // Parse an escape sequence starting at data[start] (an ESC). Returns the number
  // of characters consumed.
  handleEscape(data: string, start: number): number {
    const next = data[start + 1];
    if (next === '[' || next === 'O') {
      let j = start + 2;
      let params = '';
      while (j < data.length && !/[A-Za-z~]/.test(data[j])) params += data[j++];
      const final = data[j];
      switch (final) {
        case 'A':
          this.historyPrev();
          break; // Up
        case 'B':
          this.historyNext();
          break; // Down
        case 'C':
          params === '1;5' ? this.wordRight() : this.moveTo(this.cursor + 1);
          break; // Right / Ctrl-Right
        case 'D':
          params === '1;5' ? this.wordLeft() : this.moveTo(this.cursor - 1);
          break; // Left / Ctrl-Left
        case 'H':
          this.moveTo(0);
          break; // Home
        case 'F':
          this.moveTo(this.lineBuffer.length);
          break; // End
        case '~':
          if (params === '3')
            this.deleteChar(); // Delete
          else if (params === '1' || params === '7') this.moveTo(0);
          else if (params === '4' || params === '8') this.moveTo(this.lineBuffer.length);
          break;
      }
      return (j < data.length ? j + 1 : j) - start;
    }
    if (next === 'b') {
      this.wordLeft();
      return 2;
    } // Alt-B
    if (next === 'f') {
      this.wordRight();
      return 2;
    } // Alt-F
    return next === undefined ? 1 : 2; // lone ESC, or skip an unknown ESC-x
  }

  // Replace the whole input line with `buf` and put the cursor at `cur`, redrawn
  // relative to the input start so the prompt is untouched.
  setLine(buf: string, cur: number) {
    if (this.cursor > 0) this.term.write(`\x1b[${this.cursor}D`); // to input start
    this.term.write(`${buf}\x1b[K`); // rewrite line, clear any leftover tail
    const tail = buf.length - cur;
    if (tail > 0) this.term.write(`\x1b[${tail}D`);
    this.lineBuffer = buf;
    this.cursor = cur;
  }

  // Move the cursor (only) to absolute position `to`, clamped to the line.
  moveTo(to: number) {
    to = Math.max(0, Math.min(to, this.lineBuffer.length));
    if (to < this.cursor) this.term.write(`\x1b[${this.cursor - to}D`);
    else if (to > this.cursor) this.term.write(`\x1b[${to - this.cursor}C`);
    this.cursor = to;
  }

  // Redraw from the cursor to the end of line and leave the cursor where it
  // started. Used by single-character edits so we never repaint from the input
  // start, which would make the cursor visibly flash back to column 0.
  redrawTail() {
    const tail = this.lineBuffer.slice(this.cursor);
    this.term.write(`${tail}\x1b[K`); // rewrite tail, clear any leftover char
    if (tail.length > 0) this.term.write(`\x1b[${tail.length}D`);
  }

  insert(ch: string) {
    const b = this.lineBuffer;
    this.lineBuffer = b.slice(0, this.cursor) + ch + b.slice(this.cursor);
    this.term.write(ch);
    this.cursor += ch.length;
    this.redrawTail();
  }

  backspace() {
    if (this.cursor > 0) {
      const b = this.lineBuffer;
      this.lineBuffer = b.slice(0, this.cursor - 1) + b.slice(this.cursor);
      this.cursor -= 1;
      this.term.write('\b'); // step left over the deleted char
      this.redrawTail();
    }
  }

  deleteChar() {
    if (this.cursor < this.lineBuffer.length) {
      const b = this.lineBuffer;
      this.lineBuffer = b.slice(0, this.cursor) + b.slice(this.cursor + 1);
      this.redrawTail();
    }
  }

  killWordBack() {
    let s = this.cursor;
    while (s > 0 && this.lineBuffer[s - 1] === ' ') s--;
    while (s > 0 && this.lineBuffer[s - 1] !== ' ') s--;
    this.setLine(this.lineBuffer.slice(0, s) + this.lineBuffer.slice(this.cursor), s);
  }

  wordLeft() {
    let p = this.cursor;
    while (p > 0 && this.lineBuffer[p - 1] === ' ') p--;
    while (p > 0 && this.lineBuffer[p - 1] !== ' ') p--;
    this.moveTo(p);
  }

  wordRight() {
    let p = this.cursor;
    const len = this.lineBuffer.length;
    while (p < len && this.lineBuffer[p] === ' ') p++;
    while (p < len && this.lineBuffer[p] !== ' ') p++;
    this.moveTo(p);
  }

  historyPrev() {
    if (this.history.length === 0) return;
    if (this.histIndex === this.history.length) this.draft = this.lineBuffer;
    if (this.histIndex > 0) {
      this.histIndex -= 1;
      const e = this.history[this.histIndex];
      this.setLine(e, e.length);
    }
  }

  historyNext() {
    if (this.histIndex >= this.history.length) return;
    this.histIndex += 1;
    const e = this.histIndex === this.history.length ? this.draft : this.history[this.histIndex];
    this.setLine(e, e.length);
  }

  onCtrlC() {
    this.term.write('^C');
    this.lineBuffer = '';
    this.cursor = 0;
    this.histIndex = this.history.length;
    this.backend.onCtrlC();
  }

  commitLine() {
    this.moveTo(this.lineBuffer.length); // so trailing text isn't clobbered
    this.term.write('\r\n');
    const line = this.lineBuffer;
    if (line.length > 0 && this.history[this.history.length - 1] !== line) {
      this.history.push(line);
    }
    this.histIndex = this.history.length;
    this.draft = '';
    this.lineBuffer = '';
    this.cursor = 0;
    this.backend.sendLine(line);
  }

  ////////////////////////////////////////////////////////////////////////////
  // Run / stop

  async run(code: string, dataFiles: DataFile[]): Promise<void> {
    if (!this.usable) return;

    // Re-running while a program is still going: tear the old run down so the
    // new one starts clean.
    if (this.state === 'running') this.backend.hardStop();

    await this.backend.whenReady();

    this.term.reset();
    this.lineBuffer = '';
    this.cursor = 0;
    this.histIndex = this.history.length; // keep history across runs; reset browse pos
    this.draft = '';
    this.state = 'running';
    this.term.focus();

    return new Promise<void>((resolve) => {
      this.runResolve = resolve;
      this.backend.start(code, dataFiles);
    });
  }

  finishRun() {
    if (this.finishMessage !== null && this.state === 'running') {
      this.term.writeln(this.finishMessage);
    }
    this.state = 'idle';
    const resolve = this.runResolve;
    this.runResolve = null;
    if (resolve) resolve();
  }

  stop() {
    if (this.state !== 'running') return;
    this.backend.stop();
  }

  hardStop() {
    this.backend.hardStop();
  }
}
